import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { MdOutlineEmail } from "react-icons/md";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./Navbar.css";
import { searchIndex } from "../data/searchIndex";
import { AuthModal } from "./AuthModal";
import { memoryStorage } from "../utils/memoryStorage";
import axios from "axios";
import { SERVICE_ROUTES } from "../constants/serviceRoutes";

const SEARCH_OPEN_STORAGE_KEY = "blackrothSearchOpen";
const SEARCH_PATH_STORAGE_KEY = "blackrothSearchPath";
const contactRelationshipOptions = [
  "Client/Prospect",
  "Partner/Alliance",
  "Investor/Analyst",
  "Media/Journalist",
  "Candidate/Student/Academic",
  "Other",
];

const Navbar = ({ onLoginSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const isShareholdersPage =
    location.pathname === "/letter-to-shareholders" ||
    location.pathname.startsWith("/letter-to-shareholders/");

  const storedUser = (() => {
    try {
      return JSON.parse(memoryStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const role = (storedUser?.role || "").toUpperCase();
  // FIX: cookie-based auth, so don't depend on localStorage token
  const isCandidateLoggedIn = role === "CANDIDATE";

  // FIX: keep candidate routes consistent
  const isCandidateCareerPage =
    location.pathname === "/candidate/candidatecareer" ||
    /^\/candidate\/candidatecareer\/[\w-]+$/.test(location.pathname);

  const isJobDetailsOrApplyPage =
    /^\/careers\/[^/]+$/.test(location.pathname) ||
    /^\/apply\/[^/]+$/.test(location.pathname) ||
    /^\/candidate\/candidatecareer\/[\w-]+$/.test(location.pathname);

  const showMyApplications =
    isCandidateLoggedIn && (isCandidateCareerPage || isJobDetailsOrApplyPage);

  /* ================= STATE ================= */

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const searchInputRef = useRef(null);
  const hasRestoredSearchRef = useRef(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isAnnouncementVisible, setIsAnnouncementVisible] = useState(true);
  const [announcementScrollOffset, setAnnouncementScrollOffset] = useState(0);
  const shouldShowAnnouncement =
    isAnnouncementVisible && !isShareholdersPage;

  /* ======== FORM STATE ======== */

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    relationship: "",
    projectDetails: "",
  });

  const [errors, setErrors] = useState({});
  const relationshipOptions = contactRelationshipOptions;

  /* ================= TOGGLES ================= */

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const openSearch = () => {
    setIsSearchOpen(true);
    sessionStorage.setItem(SEARCH_OPEN_STORAGE_KEY, "true");
    sessionStorage.setItem(SEARCH_PATH_STORAGE_KEY, location.pathname);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setQuery("");
    setResults([]);
    sessionStorage.removeItem(SEARCH_OPEN_STORAGE_KEY);
    sessionStorage.removeItem(SEARCH_PATH_STORAGE_KEY);
  };

  const handleShareholdersLinkClick = () => {
    closeSearch();
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLoginClick = () => {
    setIsAuthModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleGetStartedClick = () => {
    setIsFormOpen(true);
    setIsMenuOpen(false);
    navigate("/contact-us");
  };

  const closeGetStartedForm = () => {
    setIsFormOpen(false);

    if (location.pathname === "/contact-us") {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /* ================= FORM HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  /* ================= VALIDATION ================= */

  const validateForm = () => {
    let newErrors = {};

    const nameRegex = /^[A-Za-z]{2,}(?:\s[A-Za-z]+)*$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const companyRegex = /^[A-Za-z0-9\s&.,'-]{3,}$/;

    const cleanedPhone = phone.replace(/\D/g, "");

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (!nameRegex.test(formData.firstName)) {
      newErrors.firstName = "Enter a valid first name (minimum 2 letters)";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (!nameRegex.test(formData.lastName)) {
      newErrors.lastName = "Enter a valid last name (minimum 2 letters)";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!phone) {
      newErrors.phone = "Phone number is required";
    } else if (cleanedPhone.length < 10) {
      newErrors.phone = "Enter a valid phone number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    } else if (!companyRegex.test(formData.company)) {
      newErrors.company = "Enter a valid company name (minimum 3 characters)";
    }

    if (!formData.relationship.trim()) {
      newErrors.relationship = "Relationship is required";
    } else if (!relationshipOptions.includes(formData.relationship.trim())) {
      newErrors.relationship = "Please select a valid relationship option";
    }

    if (!formData.projectDetails.trim()) {
      newErrors.projectDetails = "Project details are required";
    } else if (formData.projectDetails.trim().length < 30) {
      newErrors.projectDetails =
        "Project details must be at least 30 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      ...formData,
      phone: `+${phone}`,
    };
    console.log(payload)

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/client/contact`,
        payload,
      );

      if (res.data.success) {
        alert("Message sent successfully!");

        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          relationship: "",
          projectDetails: "",
        });

        setPhone("");
        setErrors({});
        closeGetStartedForm();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const formattedErrors = {};

        backendErrors.forEach((err) => {
          formattedErrors[err.field] = err.message;
        });

        setErrors(formattedErrors);
      } else {
        alert("Failed to send message. Please try again.");
      }
    }
  };

  /* ================= SEARCH FILTER ================= */

  useEffect(() => {
    const searchTerm = query.trim().toLowerCase();

    if (searchTerm.length < 3) {
      setResults([]);
      return;
    }

    const filtered = searchIndex.filter((item) =>
      item.title.toLowerCase().includes(searchTerm),
    );

    setResults(filtered);
  }, [query]);

  /* ================= ESC KEY ================= */

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        closeSearch();
        setIsMenuOpen(false);
        setIsFormOpen(false);

        if (location.pathname === "/contact-us") {
          navigate("/");
        }
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [location.pathname, navigate]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    if (isFormOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isFormOpen]);

  useEffect(() => {
    if (hasRestoredSearchRef.current) {
      return;
    }

    hasRestoredSearchRef.current = true;

    if (sessionStorage.getItem(SEARCH_OPEN_STORAGE_KEY) !== "true") {
      return;
    }

    const storedPath = sessionStorage.getItem(SEARCH_PATH_STORAGE_KEY);

    setIsSearchOpen(true);

    if (storedPath && storedPath !== location.pathname) {
      navigate(storedPath, { replace: true });
    }

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  }, [location.pathname, navigate]);

  /* ================= SCROLL ================= */

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 10);
      setAnnouncementScrollOffset(Math.min(scrollY, 48));
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ================= RESIZE ================= */

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleOpenGetStarted = () => {
      setIsFormOpen(true);
      setIsMenuOpen(false);
      navigate("/contact-us");
    };

    window.addEventListener("openGetStarted", handleOpenGetStarted);

    return () => {
      window.removeEventListener("openGetStarted", handleOpenGetStarted);
    };
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === "/contact-us") {
      setIsFormOpen(true);
      setIsMenuOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/" && location.state?.openMenu) {
      setIsMenuOpen(true);
      navigate("/", { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const handleResultClick = (path) => {
    closeSearch();
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goTo = (path) => {
    setIsMenuOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderSearchTitle = (title) => {
    const searchTerm = query.trim();

    if (!searchTerm) {
      return title;
    }

    const titleLower = title.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    const parts = [];
    let currentIndex = 0;
    let matchIndex = titleLower.indexOf(searchLower, currentIndex);

    while (matchIndex !== -1) {
      if (matchIndex > currentIndex) {
        parts.push(title.slice(currentIndex, matchIndex));
      }

      parts.push(
        <span className="search-match" key={`${title}-${matchIndex}`}>
          {title.slice(matchIndex, matchIndex + searchTerm.length)}
        </span>,
      );

      currentIndex = matchIndex + searchTerm.length;
      matchIndex = titleLower.indexOf(searchLower, currentIndex);
    }

    if (currentIndex < title.length) {
      parts.push(title.slice(currentIndex));
    }

    return parts;
  };

  const isLG = windowWidth >= 1024;
  const isMD = windowWidth >= 768 && windowWidth < 1024;
  const isMobileNav = windowWidth < 768;
  const showMobileHeaderActions = isMobileNav;
  const searchBrandText = "BlackRoth";
  const [searchLetterOrder] = useState(() => {
    const order = Array.from(
      { length: searchBrandText.length },
      (_, index) => index,
    );

    for (let index = order.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [order[index], order[randomIndex]] = [order[randomIndex], order[index]];
    }

    return order;
  });

  return (
    <>
      {!isShareholdersPage && (
        <div
          className={`top-announcement ${shouldShowAnnouncement ? "visible" : "hidden"}`}
          style={{
            transform: shouldShowAnnouncement
              ? `translateY(-${announcementScrollOffset}px)`
              : "translateY(-100%)",
            opacity: shouldShowAnnouncement ? 1 : 0,
          }}
        >
          <div className="top-announcement-content">
            <div className="top-announcement-text">
              <span>Read CEO </span>
              <Link
                to="/letter-to-shareholders"
                className="top-announcement-link"
                onClick={handleShareholdersLinkClick}
              >
                Letter to Shareholders
              </Link>
            </div>

            <button
              type="button"
              className="top-announcement-close"
              onClick={() => setIsAnnouncementVisible(false)}
              aria-label="Close announcement"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* ================= NAVBAR ================= */}

      <header
        className={`main-navbar ${isMobileNav ? "mobile-navbar-compact" : ""}`}
        style={{
          height: "55px",
          minHeight: "55px", 
          position: "fixed",
          top: shouldShowAnnouncement
            ? 15 + Math.max(0, 47 - announcementScrollOffset)
            : 15,
          left: isMobileNav ? 8 : 12,
          right: isMobileNav ? 8 : 12,
          zIndex: 50,
          background: isScrolled ? "rgba(255,255,255,0.85)" : "transparent",
          backdropFilter: isScrolled ? "blur(12px)" : "none",
          boxShadow: isScrolled ? "0 4px 20px rgba(0,0,0,0.1)" : "none",
          transition: "all 0.3s ease",
          padding: isMobileNav ? "5px 12px" : "5px 26px",
        }}
      >
        <div className="nav-left">
          <Link to="/" className="brand-logo">
            <span className="brand-logo-text">
              BlackRoth<span className="trademark">TM</span>
            </span>
          </Link>
        </div>

        <div className="nav-right">
          {!isMobileNav && showMyApplications && (
            <button
              className="get-started"
              onClick={() =>
                navigate("/candidate/candidatedashboard?tab=overview")
              }
            >
              My Applications
            </button>
          )}

          {!isMobileNav && (isLG || isMD) && !isHomePage && !showMyApplications && (
            <button className="get-started" onClick={handleLoginClick}>
              Login
            </button>
          )}

          {!isMobileNav && isHomePage && (
            <>
              <button className="get-started" onClick={handleLoginClick}>
                Login
              </button>

              <button
                className="get-started"
                onClick={handleGetStartedClick}
                aria-label="Get Started"
              >
                <MdOutlineEmail size={20} aria-hidden="true" />
              </button>
            </>
          )}

          {showMobileHeaderActions && (
            <>
              {showMyApplications ? (
                <button
                  className="get-started mobile-inline-action"
                  onClick={() =>
                    navigate("/candidate/candidatedashboard?tab=overview")
                  }
                >
                  My Applications
                </button>
              ) : (
                <button
                  className="get-started mobile-inline-action"
                  onClick={handleLoginClick}
                >
                  Login
                </button>
              )}

              {isHomePage && (
                <button
                  className="get-started mobile-inline-action mobile-inline-icon"
                  onClick={handleGetStartedClick}
                  aria-label="Get Started"
                >
                  <MdOutlineEmail size={18} aria-hidden="true" />
                </button>
              )}
            </>
          )}

          <button
            className="search-toggle"
            onClick={openSearch}
            aria-label="Open search"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Search size={20} strokeWidth={2.2} aria-hidden="true" />
            <i
              className="fa-solid fa-magnifying-glass"
              aria-hidden="true"
              style={{ display: "none" }}
            ></i>
          </button>

          <button className="hamburger" onClick={toggleMenu}>
            &#9776;
          </button>
        </div>

      </header>

      {/* ================= FULLSCREEN MENU ================= */}
      <div className={`fullscreen-menu ${isMenuOpen ? "active" : ""}`}>
        <button className="close-fullscreen" onClick={toggleMenu}>
          &times;
        </button>

        <div className="fullscreen-menu-content">
          <button onClick={() => goTo(SERVICE_ROUTES.index)} className="menu-main-btn">
            Our Services
          </button>

          <button onClick={() => goTo("/research-and-development")} className="menu-main-btn">
            Research & Development
          </button>
          <button
            onClick={() => goTo("/object-detection")}
            className="menu-main-btn"
          >
            Object Detection & Tracking
          </button>
          <button
            onClick={() => goTo("/autonomous-navigation")}
            className="menu-main-btn"
          >
            Autonomous Navigation
          </button>
          <button
            onClick={() => goTo("/edge-computing")}
            className="menu-main-btn"
          >
            Edge Computing
          </button>
          <button
            onClick={() => goTo("/iot-integration")}
            className="menu-main-btn"
          >
            IoT Integration
          </button>

          {isMobileNav && showMyApplications && (
            <div className="mobile-menu-actions">
              <button
                className="menu-main-btn"
                onClick={() =>
                  goTo("/candidate/candidatedashboard?tab=overview")
                }
              >
                My Applications
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ================= SEARCH OVERLAY ================= */}
      <div className={`search-overlay ${isSearchOpen ? "active" : ""}`}>
        <div className="search-box">
          <h1 className="search-brand-title" aria-label={searchBrandText}>
            {searchBrandText.split("").map((letter, index) => (
              <span
                key={`${letter}-${index}`}
                className={`search-brand-letter ${
                  index === searchBrandText.length - 1
                    ? "search-brand-letter-last"
                    : ""
                }`}
                style={{
                  "--search-letter-delay": `${searchLetterOrder[index] * 0.08}s`,
                }}
                aria-hidden="true"
              >
                {letter}
                {index === searchBrandText.length - 1 && (
                  <span className="search-brand-trademark">
                    <span className="search-brand-trademark-text">TM</span>
                  </span>
                )}
              </span>
            ))}
          </h1>
          <div className="search-input-wrap">
            <Search size={25} strokeWidth={2} aria-hidden="true" />
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search services, pages..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              className="close-btn"
              onClick={closeSearch}
              aria-label="Close search"
            >
              &times;
            </button>
          </div>

          <div className="search-results">
            {query && results.length === 0 && (
              <p className="no-results">No results found</p>
            )}

            {results.map((item, index) => (
              <button
                key={index}
                className="search-result-item"
                onClick={() => handleResultClick(item.path)}
              >
                {renderSearchTitle(item.title)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ================= GET STARTED FORM ================= */}

      {isFormOpen && (
        <div className="getstarted-overlay">
          <div className="getstarted-form">
            <button
              className="form-close-btn"
              onClick={closeGetStartedForm}
            >
              ×
            </button>

            <h2>
              Get Started with BlackRoth<span className="trademark">TM</span>
            </h2>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>

                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />

                {errors.firstName && (
                  <span className="error">{errors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label>Last Name *</label>

                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />

                {errors.lastName && (
                  <span className="error">{errors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />

                {errors.email && <span className="error">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Phone *</label>

                <PhoneInput
                  country="in"
                  value={phone}
                  onChange={setPhone}
                  enableSearch
                  inputStyle={{ width: "100%" }}
                />

                {errors.phone && <span className="error">{errors.phone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Company / Organization *</label>

              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
              />

              {errors.company && (
                <span className="error">{errors.company}</span>
              )}
            </div>

            <div className="form-group">
              <label>Relationship *</label>

              <select
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
              >
                <option value="">Select an option</option>

                {relationshipOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              {errors.relationship && (
                <span className="error">{errors.relationship}</span>
              )}
            </div>

            <div className="form-group">
              <label>Project Details *</label>

              <textarea
                name="projectDetails"
                value={formData.projectDetails}
                onChange={handleChange}
              />

              {errors.projectDetails && (
                <span className="error">{errors.projectDetails}</span>
              )}
            </div>

            <button className="send-btn" onClick={handleSubmit}>
              Send Message
            </button>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={onLoginSuccess}
      />
    </>
  );
};

export default Navbar;
 
